//
// Copyright (c) 2023 Emplifi, Inc.
//

import {
  type Editor,
  findAttributeRange,
  LinkCommand,
  type Model,
  type ModelRange,
  type ModelWriter,
  UnlinkCommand,
} from "ckeditor5";
import { ATTRIBUTE_CONTENT_MAPPING, ATTRIBUTE_LINK_TOPIC, ATTRIBUTE_LINK_TYPE } from "./util";

export default class AkUnlinkCommand extends UnlinkCommand {
  constructor(editor: Editor) {
    super(editor);
  }

  public execute() {
    // Need to override to allow removing additional attributes, not just linkHref
    const editor = this.editor;
    const model = this.editor.model;
    const linkCommand = editor.commands.get("link") as LinkCommand;
    model.change((writer) => {
      // Get ranges to unlink.
      const rangesToUnlink = this.findLinkRanges(model);
      // Remove `linkHref` attribute from specified ranges.
      for (const range of rangesToUnlink) {
        this.removeLinkAttributes(writer, range, linkCommand);
      }
    });
  }

  private findLinkRanges(model: Model) {
    const selection = model.document.selection;
    if (selection.isCollapsed) {
      return [
        findAttributeRange(
          selection.getFirstPosition()!,
          "linkHref",
          selection.getAttribute("linkHref"),
          model,
        ),
      ];
    }
    return model.schema.getValidRanges(selection.getRanges(), "linkHref");
  }

  protected removeLinkAttributes(writer: ModelWriter, range: ModelRange, linkCommand: LinkCommand) {
    writer.removeAttribute("linkHref", range);
    writer.removeAttribute(ATTRIBUTE_CONTENT_MAPPING, range);
    writer.removeAttribute(ATTRIBUTE_LINK_TOPIC, range);
    writer.removeAttribute(ATTRIBUTE_LINK_TYPE, range);
    // If there are registered custom attributes, then remove them during when removing the link
    if (linkCommand) {
      for (const manualDecorator of linkCommand.manualDecorators) {
        writer.removeAttribute(manualDecorator.id, range);
      }
    }
  }
}
